import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { hasPendingTranslation } from '@/component/multiLanguage/handlers/ChatPostOnDemandHandler';

const { CHAT_SCROLL_MODE } = Constant;

/**
 * First (top-most on screen) message the user has not read yet: posted by someone else, not deleted,
 * and the user is not in its footprint. The DOM order is used because each chat screen sorts its list
 * differently (e.g. email ticket).
 * @param {HTMLElement} container
 * @param {Array} chatPostList - posts rendered in the container
 * @param {string} userId - current user id
 * @returns {HTMLElement|null}
 */
const findFirstUnreadMessage = (container, chatPostList, userId) => {
    if (!userId) return null;
    const postById = new Map(chatPostList.map(post => [post?.id, post]));
    const deletedStatusList = [Constant.CHAT_POST_STATUS.DELETE, Constant.CHAT_POST_STATUS.DELETE_READ];
    const unreadEl = [...container.querySelectorAll('.chatBox-text[id]')].find(el => {
        const post = postById.get(el.id);
        return (
            post &&
            post.creatorId !== userId &&
            !deletedStatusList.includes(post.status) &&
            !post.footPrint?.userList?.includes(userId)
        );
    });
    return unreadEl || null;
};

/**
 * useKeepChatScrollOnViewChange
 *
 * Single owner of the automatic scroll of a chat panel. Each case has its own effect:
 *   Case 1 (first open): once per loaded list, scroll to the first unread message (shown at the top),
 *           or to the bottom when everything is read, when no message is focused.
 *   Case 2 (focus message): scroll the focused message to the center of the viewport.
 *           While translations are still in flight the focus waits (pending focus) until
 *           they finish or a timeout passes, so the focus scroll does not fight the
 *           translation restore.
 *   Case 3 (translation restore): when the translation view changes, keep the message
 *           at the top of the viewport at the same position (snapshot / restore).
 *
 * `scrollModeRef` tells who currently owns the scroll:
 *   IDLE             - nobody; a user scroll updates the snapshot.
 *   BOTTOM           - scrollToBottom is moving the list (until the next frame).
 *   FOCUS            - a focus is pending (waiting for translations) or the focus scroll is running.
 *   KEEP_TRANSLATION - Case 3 is restoring the position (until the next frame).
 *
 * All refs are per panel instance (no module-level flags shared between panels).
 * `scrollResetKey` changes whenever the list is reloaded (initial load, reload, thread
 * switch); it resets the scroll state so the new list gets its own initial position.
 *
 * @param {Object} params
 * @param {string} params.panelId          - panel id (used to build the chat container id).
 * @param {string} params.chatThreadId     - id of the open thread.
 * @param {Object} params.chatPostOnDemand - per-thread/per-message translation view state (Recoil).
 * @param {Array}  params.chatPostList     - posts currently rendered in the panel.
 * @param {string} [params.focusedMsgId]   - message to focus (if any).
 * @param {*}      [params.scrollResetKey] - changes when the post list is reloaded.
 * @param {string} [params.currentUserId]  - current user id (to find the first unread message).
 * @returns {{ onChatScroll: Function, scrollToBottom: Function, focusMessage: Function, isProgrammaticScroll: Function, isFocusScrolling: Function }}
 */
const useKeepChatScrollOnViewChange = ({
    panelId,
    chatThreadId,
    chatPostOnDemand,
    chatPostList,
    focusedMsgId,
    scrollResetKey,
    currentUserId
}) => {
    // Id of the scroll container holding the message list.
    const chatContentListId = `divChatContentList-${panelId}-${chatThreadId}`;

    // Snapshot of the scroll position captured on the latest user scroll.
    const scrollAnchorRef = useRef(null);
    // Who currently owns the scroll (see CHAT_SCROLL_MODE above).
    const scrollModeRef = useRef(CHAT_SCROLL_MODE.IDLE);
    // True while the smooth focus scroll is running.
    const isFocusingRef = useRef(false);
    // Case 1 runs once per loaded list.
    const hasInitialScrolledRef = useRef(false);
    // List rendered when the reset happened (stale until the new list is rendered).
    const staleListRef = useRef(null);
    // Message already focused for the current list (avoid re-focusing on every render).
    const focusedDoneIdRef = useRef(null);
    // Latest focusedMsgId, read by scrollToBottom without re-creating it.
    const focusedMsgIdRef = useRef(focusedMsgId);
    focusedMsgIdRef.current = focusedMsgId;
    const focusSettleTimerRef = useRef(null);
    // Time the current focus scroll started (caps the guard at CHAT_FOCUS_SETTLE_MAX_MS).
    const focusStartedAtRef = useRef(0);
    const pendingTimerRef = useRef(null);
    // Focus id the pending-translation timer runs for (a timer of another focus is restarted).
    const pendingFocusIdRef = useRef(null);
    const bottomRafRef = useRef(null);
    // Focus id whose pending-translation wait timed out; only that focus skips the wait.
    const [timedOutFocusId, setTimedOutFocusId] = useState(null);

    const getContainer = useCallback(() => document.getElementById(chatContentListId), [chatContentListId]);

    /**
     * Snapshot the current scroll position.
     * - Near the bottom: remember "at bottom".
     * - Otherwise: remember the first message in the viewport and its offset from the top.
     */
    const captureScrollAnchor = useCallback(() => {
        const container = getContainer();
        if (!container) return;

        // Treat as "at bottom" when within the threshold from the bottom.
        const isAtBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight <= Constant.CHAT_SCROLL_BOTTOM_THRESHOLD;

        if (isAtBottom) {
            scrollAnchorRef.current = { isAtBottom: true, anchorId: null, offsetInViewport: 0 };
            return;
        }

        // Find the message sitting at the top of the viewport to use as the anchor.
        const items = container.querySelectorAll('.chatBox-text[id]');
        const viewportTop = container.scrollTop;
        let anchor = null;
        for (const el of items) {
            // The first message whose top edge is >= the viewport top is the anchor.
            if (el.offsetTop >= viewportTop - 1) {
                anchor = el;
                break;
            }
            anchor = el;
        }
        if (!anchor) {
            scrollAnchorRef.current = null;
            return;
        }

        scrollAnchorRef.current = {
            isAtBottom: false,
            anchorId: anchor.id,
            // Distance from the message top to the viewport top (kept constant on restore).
            offsetInViewport: anchor.offsetTop - viewportTop
        };
    }, [getContainer]);

    /**
     * Restore the scroll position from the captured snapshot.
     */
    const restoreScrollAnchor = () => {
        const snapshot = scrollAnchorRef.current;
        if (!snapshot) return;

        const container = getContainer();
        if (!container) return;

        if (snapshot.isAtBottom) {
            container.scrollTop = container.scrollHeight;
            return;
        }

        if (!snapshot.anchorId) return;
        const anchorEl = document.getElementById(snapshot.anchorId);
        if (!anchorEl) return;

        // Reset scroll so the anchor message returns to its old distance from the viewport top.
        container.scrollTop = anchorEl.offsetTop - snapshot.offsetInViewport;
    };

    const clearFocusTimers = () => {
        clearTimeout(focusSettleTimerRef.current);
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
        pendingFocusIdRef.current = null;
    };

    /**
     * Scroll the list to the bottom and remember "at bottom" as the anchor.
     * An explicit "go to bottom" (new own message, jump button, AI answer) wins over a pending
     * or running focus: the focus is cancelled so it does not pull the list back later.
     */
    const scrollToBottom = useCallback(() => {
        const container = getContainer();
        if (!container) return;

        if (scrollModeRef.current === CHAT_SCROLL_MODE.FOCUS) {
            clearTimeout(focusSettleTimerRef.current);
            clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = null;
            pendingFocusIdRef.current = null;
            isFocusingRef.current = false;
            focusedDoneIdRef.current = focusedMsgIdRef.current;
            // The list is now placed at the bottom explicitly: Case 1 must not run again later.
            hasInitialScrolledRef.current = true;
        }

        scrollModeRef.current = CHAT_SCROLL_MODE.BOTTOM;
        container.scrollTop = container.scrollHeight;
        scrollAnchorRef.current = { isAtBottom: true, anchorId: null, offsetInViewport: 0 };

        // Release on the next frame, after the scroll event produced by this move.
        cancelAnimationFrame(bottomRafRef.current);
        bottomRafRef.current = requestAnimationFrame(() => {
            if (scrollModeRef.current === CHAT_SCROLL_MODE.BOTTOM) {
                scrollModeRef.current = CHAT_SCROLL_MODE.IDLE;
            }
        });
    }, [getContainer]);

    /**
     * End of the focus scroll: release the scroll ownership and re-capture the anchor.
     */
    const settleFocus = useCallback(() => {
        clearTimeout(focusSettleTimerRef.current);
        isFocusingRef.current = false;
        scrollModeRef.current = CHAT_SCROLL_MODE.IDLE;
        captureScrollAnchor();
        // Load-more was skipped while the focus scroll reached the top; the user is already at
        // scrollTop 0 so no new scroll event comes. Fire one so the container's onScroll loads older posts.
        const container = getContainer();
        if (container && container.scrollTop === 0) {
            container.dispatchEvent(new Event('scroll'));
        }
    }, [captureScrollAnchor, getContainer]);

    /**
     * (Re)schedule settleFocus after `delay` ms, within
     * [CHAT_FOCUS_SETTLE_MS, CHAT_FOCUS_SETTLE_MAX_MS] from the start of the focus scroll.
     * The lower bound keeps the guard at least as long as the fixed settle time used before.
     * @param {number} delay
     */
    const scheduleFocusSettle = useCallback(delay => {
        clearTimeout(focusSettleTimerRef.current);
        const elapsed = Date.now() - focusStartedAtRef.current;
        const minDelay = Constant.CHAT_FOCUS_SETTLE_MS - elapsed;
        const maxDelay = Constant.CHAT_FOCUS_SETTLE_MAX_MS - elapsed;
        focusSettleTimerRef.current = setTimeout(
            settleFocus,
            Math.max(0, Math.min(Math.max(delay, minDelay), maxDelay))
        );
    }, [settleFocus]);

    /**
     * Scroll a message to the center of the viewport. Translation restore is paused until
     * the smooth scroll settles, then the anchor is re-captured at the new position.
     * The scroll is settled CHAT_FOCUS_SETTLE_MS after the start when no scroll event comes
     * (the message was already in place); while it moves, each scroll event pushes the settle
     * to CHAT_FOCUS_SCROLL_IDLE_MS later (see onChatScroll), so a long smooth scroll is not cut.
     * Only the chat list holding the message is scrolled: scrollIntoView would also scroll every
     * scrollable ancestor (e.g. the panel list when more than 3 panels overflow), which breaks the layout.
     * The list is taken from the message itself (like scrollIntoView), not from this panel: the id is
     * looked up in the whole document, so the element may sit in another panel's list.
     * @param {string} msgId
     * @returns {boolean} true when the message element exists and the scroll started
     */
    const focusMessage = useCallback(msgId => {
        const el = msgId && document.getElementById(msgId);
        const container = el && (el.closest('[id^="divChatContentList-"]') || getContainer());
        if (!el || !container) return false;

        scrollModeRef.current = CHAT_SCROLL_MODE.FOCUS;
        isFocusingRef.current = true;
        focusStartedAtRef.current = Date.now();
        // Same position as scrollIntoView({ block: 'center' }), relative to the container only.
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const top =
            container.scrollTop + (elRect.top - containerRect.top) - (container.clientHeight - elRect.height) / 2;
        container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

        scheduleFocusSettle(Constant.CHAT_FOCUS_SETTLE_MS);
        return true;
    }, [getContainer, scheduleFocusSettle]);

    /**
     * Reset: the post list was reloaded, so the old anchor / initial / focus state is invalid.
     * Declared before the case effects so it runs first in the same commit.
     */
    useLayoutEffect(() => {
        clearFocusTimers();
        cancelAnimationFrame(bottomRafRef.current);
        scrollAnchorRef.current = null;
        isFocusingRef.current = false;
        scrollModeRef.current = CHAT_SCROLL_MODE.IDLE;
        hasInitialScrolledRef.current = false;
        focusedDoneIdRef.current = null;
        staleListRef.current = chatPostList;
        setTimedOutFocusId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollResetKey]);

    // Clear timers on unmount.
    useEffect(
        () => () => {
            clearFocusTimers();
            cancelAnimationFrame(bottomRafRef.current);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    /**
     * Case 1: first open of a freshly loaded list -> once, scroll to the first unread message,
     * or to the bottom when everything is read.
     * Skipped when the focused message is rendered (Case 2 owns the position). A focus id without
     * an element cannot be focused, so it does not block: it may belong to a hidden thread, or be in
     * the list but not rendered (e.g. DELETE_READ in an email ticket, empty post).
     */
    useEffect(() => {
        if (hasInitialScrolledRef.current) return;
        if (Utils.isEmptyArray(chatPostList) || chatPostList === staleListRef.current) return;
        if (focusedMsgId && document.getElementById(focusedMsgId)) return;
        const container = getContainer();
        if (!container) return;

        const unreadEl = findFirstUnreadMessage(container, chatPostList, currentUserId);
        if (unreadEl) {
            container.scrollTop = unreadEl.offsetTop;
            captureScrollAnchor();
        } else {
            scrollToBottom();
        }
        hasInitialScrolledRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatPostList, focusedMsgId, scrollResetKey]);

    // Threads shown in the panel; used to scope the pending-translation check.
    const displayedThreadIds = useMemo(
        () => [...new Set((chatPostList || []).map(post => post?.chatThreadId).filter(Boolean))],
        [chatPostList]
    );
    const isTranslationPending = useMemo(
        () => hasPendingTranslation(chatPostOnDemand, displayedThreadIds),
        [chatPostOnDemand, displayedThreadIds]
    );

    /**
     * Case 2: focus a message. Waits for in-flight translations (pending focus) up to a
     * timeout, so the focused message lands at its final (translated) position.
     */
    useEffect(() => {
        if (!focusedMsgId) {
            // Focus cancelled (e.g. user clicked outside): release a pending focus.
            clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = null;
            pendingFocusIdRef.current = null;
            focusedDoneIdRef.current = null;
            if (timedOutFocusId) setTimedOutFocusId(null);
            if (!isFocusingRef.current && scrollModeRef.current === CHAT_SCROLL_MODE.FOCUS) {
                scrollModeRef.current = CHAT_SCROLL_MODE.IDLE;
            }
            return;
        }
        if (focusedDoneIdRef.current === focusedMsgId) return;
        if (!document.getElementById(focusedMsgId)) return; // wait until the message is rendered

        // Note: hasInitialScrolledRef is set only once the focus scroll really starts. A focus
        // cancelled while pending (e.g. a click) must let Case 1 scroll to the initial position instead.
        if (isTranslationPending && timedOutFocusId !== focusedMsgId) {
            // Hold the scroll ownership so translation restore does not move the list meanwhile.
            scrollModeRef.current = CHAT_SCROLL_MODE.FOCUS;
            // Each focus gets its own full wait: a timer started for a previous focus is restarted.
            if (pendingFocusIdRef.current !== focusedMsgId) {
                clearTimeout(pendingTimerRef.current);
                const pendingFocusId = focusedMsgId;
                pendingFocusIdRef.current = pendingFocusId;
                pendingTimerRef.current = setTimeout(
                    () => setTimedOutFocusId(pendingFocusId),
                    Constant.CHAT_FOCUS_PENDING_TRANSLATION_TIMEOUT_MS
                );
            }
            return;
        }

        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
        pendingFocusIdRef.current = null;
        // The timeout is used up by this attempt, even if the scroll does not start:
        // the next focus (or a retry of this one) waits for translations again.
        if (timedOutFocusId) setTimedOutFocusId(null);
        if (focusMessage(focusedMsgId)) {
            focusedDoneIdRef.current = focusedMsgId;
            // A focus replaces the initial position: Case 1 must not run later (e.g. after the
            // focus is cleared by a click) and pull the list to the bottom.
            hasInitialScrolledRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedMsgId, chatPostList, isTranslationPending, timedOutFocusId, scrollResetKey]);

    /**
     * Signature that changes whenever any thread/message translation view flips on/off.
     */
    const translationViewSignature = useMemo(() => {
        const parts = [];
        for (const tid in chatPostOnDemand) {
            const record = chatPostOnDemand[tid];
            if (!record) continue;
            parts.push(`${tid}:${record.activeView}`);
            const messages = record.messages || {};
            for (const pid in messages) {
                parts.push(`${pid}:${messages[pid]?.activeView}`);
            }
        }
        return parts.join('|');
    }, [chatPostOnDemand]);

    /**
     * Case 3: when the translation view changes, keep the scroll in place (runs before paint).
     * Skipped while a focus owns the scroll (pending or in progress).
     */
    useLayoutEffect(() => {
        if (scrollModeRef.current === CHAT_SCROLL_MODE.FOCUS) return;

        scrollModeRef.current = CHAT_SCROLL_MODE.KEEP_TRANSLATION;

        // Restore #1: right before paint.
        restoreScrollAnchor();

        // Restore #2: after the layout settles (absorbs late-rendering content).
        // The anchor is NOT re-captured here: it only updates on a real user scroll.
        const rafId = requestAnimationFrame(() => {
            if (scrollModeRef.current === CHAT_SCROLL_MODE.KEEP_TRANSLATION) {
                restoreScrollAnchor();
                scrollModeRef.current = CHAT_SCROLL_MODE.IDLE;
            }
        });

        return () => {
            cancelAnimationFrame(rafId);
            if (scrollModeRef.current === CHAT_SCROLL_MODE.KEEP_TRANSLATION) {
                scrollModeRef.current = CHAT_SCROLL_MODE.IDLE;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translationViewSignature]);

    /**
     * Whether the current scroll is produced by this hook (bottom / focus / restore), not by the user.
     * A pending focus (still waiting for translations) does not move the list, so it is not counted.
     * The snapshot (onChatScroll) is not updated on such scrolls.
     */
    const isProgrammaticScroll = useCallback(
        () =>
            isFocusingRef.current ||
            scrollModeRef.current === CHAT_SCROLL_MODE.BOTTOM ||
            scrollModeRef.current === CHAT_SCROLL_MODE.KEEP_TRANSLATION,
        []
    );

    /**
     * Whether the smooth focus scroll is running. Load-more must not fire then (focusing a message
     * at the top of the list reaches scrollTop 0). Bottom / translation restore do not block
     * load-more, as before: a user reaching the top during such a frame still loads older posts.
     */
    const isFocusScrolling = useCallback(() => isFocusingRef.current, []);

    /**
     * Attach to the chat container's onScroll (and call after a programmatic scroll that
     * must become the new anchor, e.g. load-more). Skipped while our code moves the scroll.
     * During the focus scroll it only pushes the settle back (the scroll is still moving).
     */
    const onChatScroll = useCallback(() => {
        if (isFocusingRef.current) {
            scheduleFocusSettle(Constant.CHAT_FOCUS_SCROLL_IDLE_MS);
            return;
        }
        if (isProgrammaticScroll()) return;
        captureScrollAnchor();
    }, [captureScrollAnchor, isProgrammaticScroll, scheduleFocusSettle]);

    return { onChatScroll, scrollToBottom, focusMessage, isProgrammaticScroll, isFocusScrolling };
};

export default useKeepChatScrollOnViewChange;
